/**
 * Customer Detail Modal
 *
 * Shows extended customer information when a row is clicked
 * in the admin customers table. Fetches data from the
 * GET /api/admin/customers/{id} endpoint.
 *
 * Organized into sections:
 * - Identity: name, email, country
 * - Activity: signed up, last login, contacts, scheduled items
 * - Subscription: plan, status, billing, period, cancellation
 * - Attribution: first-touch ad ledger (PBI #1104)
 * - Cookie consent: Analytics/Marketing Yes/No/Unknown for Ads reconciliation (PBI #1154)
 * - Account: onboarding, terms, AI regen, contact limit override,
 *   marketing / nurture email opt-in (editable, PBI #1126)
 * - Billing risk: unpaid flower orders (PaymentCaptureFailed) with links to Flower Orders admin
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import {
  Text,
  Group,
  Stack,
  Badge,
  Divider,
  Loader,
  Center,
  Alert,
  Button,
  UnstyledButton,
  Switch,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconChevronRight, IconExternalLink } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import {
  getAdminCustomerDetail,
  updateAdminCustomerMarketingEmails,
  updateAdminCustomerAccountStatus,
  type AdminCustomerDetail,
} from '../../services/adminCustomersService';
import { brandColors, semanticColors } from '../../config/theme';
import {
  ACQUISITION_PROVIDER_OTHER,
  getAcquisitionProviderBadgeColor,
} from '../../constants/acquisitionProviders';
import { AppModal } from '../common/AppModal';
import { CustomerTestAccountSection } from './CustomerTestAccountSection';
import { DemoAccountPill } from './DemoAccountPill';
import { formatInstantLocal, formatInstantLocalDate } from '../../utils/formatDateTime';
import { useModalZIndex } from '../../hooks/useModalZIndex';

/**
 * Lazy-loaded and only mounted while smiles are open so
 * OperationEventDetailModal → CustomerDetailModal cannot recursively mount
 * CustomerScheduledSmilesModal → OperationEventDetailModal (browser OOM).
 */
const CustomerScheduledSmilesModal = lazy(() =>
  import('./CustomerScheduledSmilesModal').then((m) => ({
    default: m.CustomerScheduledSmilesModal,
  })),
);

interface CustomerDetailModalProps {
  /** The customer ID to load details for. Null when modal is closed. */
  customerId: string | null;
  /** Whether the modal is open. */
  opened: boolean;
  /** Callback to close the modal. */
  onClose: () => void;
  /** Called after account disable/re-enable so the list can refresh. */
  onAccountStatusChanged?: () => void;
}

/**
 * Returns a Mantine color for subscription status.
 */
const getStatusColor = (status: string | null): string => {
  switch (status) {
    case 'Active':
      return brandColors.success;
    case 'PastDue':
      return brandColors.warning;
    case 'Expired':
      return brandColors.danger;
    default:
      return brandColors.neutral;
  }
};

const formatDateTime = (dateStr: string | null): string => formatInstantLocal(dateStr);

const formatDate = (dateStr: string | null): string => formatInstantLocalDate(dateStr);

/**
 * Yes / No / Unknown badge for cookie preference ledger (PBI #1154).
 * Null means the SPA never posted consent after auth.
 */
const CookieConsentBadge = ({ value }: { value: boolean | null }) => {
  if (value === true) {
    return (
      <Badge color={brandColors.success} size="sm" variant="light">
        Yes
      </Badge>
    );
  }

  if (value === false) {
    return (
      <Badge color={brandColors.warning} size="sm" variant="light">
        No
      </Badge>
    );
  }

  return (
    <Badge color={brandColors.neutral} size="sm" variant="light">
      Unknown
    </Badge>
  );
};

/**
 * Ads reconciliation helper under COOKIE CONSENT when paid traffic is present.
 */
const paidTrafficConsentHelper = (
  cameViaPaidAd: boolean,
  marketingConsent: boolean | null,
): string | null => {
  if (!cameViaPaidAd) return null;

  if (marketingConsent === false) {
    return 'Paid click recorded, but marketing cookies were declined — Google Ads / OpenAI Ads conversion tags would not record.';
  }

  if (marketingConsent === null) {
    return 'Cookie choice not stored yet — cannot tell if Ads should have recorded.';
  }

  return null;
};

/**
 * A single label-value row in the detail modal.
 */
const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Group justify="space-between" wrap="nowrap">
    <Text fw={500} size="sm" style={{ flexShrink: 0 }}>
      {label}
    </Text>
    <div style={{ textAlign: 'right' }}>{children}</div>
  </Group>
);

export const CustomerDetailModal = ({
  customerId,
  opened,
  onClose,
  onAccountStatusChanged,
}: CustomerDetailModalProps) => {
  const zIndex = useModalZIndex(opened);
  const [detail, setDetail] = useState<AdminCustomerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [smilesOpened, setSmilesOpened] = useState(false);
  const [savingMarketingEmails, setSavingMarketingEmails] = useState(false);
  const [accountConfirmOpened, setAccountConfirmOpened] = useState(false);
  const [pendingAccountDisabled, setPendingAccountDisabled] = useState(false);
  const [savingAccountStatus, setSavingAccountStatus] = useState(false);

  useEffect(() => {
    if (!opened || !customerId) {
      setDetail(null);
      setError(null);
      setSmilesOpened(false);
      setSavingMarketingEmails(false);
      setAccountConfirmOpened(false);
      setPendingAccountDisabled(false);
      setSavingAccountStatus(false);
      return;
    }

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getAdminCustomerDetail(customerId);
        setDetail(data);
      } catch {
        setError('Failed to load customer details.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [opened, customerId]);

  /**
   * Immediately persists marketing / nurture opt-in (same wording as My Account).
   * Optimistic UI with rollback on failure.
   */
  const handleMarketingEmailsChange = async (nextEnabled: boolean) => {
    if (!customerId || !detail || savingMarketingEmails) {
      return;
    }

    const previous = detail.marketingEmailsEnabled;
    setDetail({ ...detail, marketingEmailsEnabled: nextEnabled });
    setSavingMarketingEmails(true);

    try {
      const updated = await updateAdminCustomerMarketingEmails(customerId, nextEnabled);
      setDetail((current) =>
        current
          ? { ...current, marketingEmailsEnabled: updated.marketingEmailsEnabled }
          : current,
      );
      notifications.show({
        title: nextEnabled ? 'Marketing emails enabled' : 'Marketing emails disabled',
        message: nextEnabled
          ? 'Customer may receive nurture emails again.'
          : 'Nurture / marketing emails will no longer be sent.',
        color: nextEnabled ? brandColors.success : brandColors.warning,
      });
    } catch {
      setDetail((current) =>
        current ? { ...current, marketingEmailsEnabled: previous } : current,
      );
      notifications.show({
        title: 'Error',
        message: 'Failed to update marketing email preference.',
        color: brandColors.danger,
      });
    } finally {
      setSavingMarketingEmails(false);
    }
  };

  const openAccountStatusConfirm = (disable: boolean) => {
    setPendingAccountDisabled(disable);
    setAccountConfirmOpened(true);
  };

  const handleAccountStatusConfirm = async () => {
    if (!customerId || !detail || savingAccountStatus) {
      return;
    }

    setSavingAccountStatus(true);

    try {
      const updated = await updateAdminCustomerAccountStatus(
        customerId,
        pendingAccountDisabled,
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              isAccountDisabled: updated.isAccountDisabled,
              accountDisabledAtUtc: updated.accountDisabledAtUtc,
            }
          : current,
      );
      setAccountConfirmOpened(false);
      onAccountStatusChanged?.();
      notifications.show({
        title: pendingAccountDisabled ? 'Account disabled' : 'Account re-enabled',
        message: pendingAccountDisabled
          ? 'The customer cannot sign in. Active subscriptions cancel at period end.'
          : 'The customer can sign in again. Pending subscription cancel may be undone.',
        color: pendingAccountDisabled ? brandColors.warning : brandColors.success,
      });
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      notifications.show({
        title: 'Error',
        message:
          axiosError.response?.data?.message ??
          'Failed to update account status. Please try again.',
        color: brandColors.danger,
      });
    } finally {
      setSavingAccountStatus(false);
    }
  };

  const statusLabel =
    detail?.subscriptionStatus === 'Active' && detail?.cancelAtPeriodEnd
      ? 'Canceling'
      : detail?.subscriptionStatus === 'PastDue'
        ? 'Past Due'
        : detail?.subscriptionStatus || 'Free';

  const adsConsentHelper = detail
    ? paidTrafficConsentHelper(detail.cameViaPaidAd, detail.cookieMarketingConsent)
    : null;

  return (
    <>
    <AppModal
      id="customer-detail"
      opened={opened}
      onClose={onClose}
      zIndex={zIndex}
      title="Customer Details"
      size="lg"
      footer={null}
    >
      {loading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : error ? (
        <Alert color={brandColors.danger} icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      ) : detail ? (
        <Stack gap="md">
          {detail.unpaidFlowerOrderCount > 0 && detail.unpaidFlowerOrders.length > 0 && (
            <Alert
              color={brandColors.danger}
              icon={<IconAlertCircle size={18} />}
              title="Unpaid flower orders (capture failed)"
            >
              <Stack gap="sm">
                <Text size="sm">
                  Stripe capture failed after the vendor accepted these orders. Resolve payment or void
                  before the customer accrues more exposure.
                </Text>
                {detail.unpaidFlowerOrders.map((o) => (
                  <Group key={o.id} justify="space-between" wrap="wrap" gap="xs">
                    <Text size="sm">
                      Order #{o.id} ({o.productName}) — updated {formatDateTime(o.updatedAt)}
                    </Text>
                    <Button
                      component={Link}
                      to={`/admin/flower-orders?orderId=${o.id}`}
                      size="xs"
                      variant="light"
                      color={brandColors.danger}
                      rightSection={<IconExternalLink size={14} />}
                    >
                      Open in Flower Orders
                    </Button>
                  </Group>
                ))}
              </Stack>
            </Alert>
          )}

          {/* Identity Section */}
          <Text fw={600} size="sm" c={semanticColors.text.secondary}>
            IDENTITY
          </Text>

          <DetailRow label="Name">
            <Group gap="xs" justify="flex-end" wrap="nowrap">
              <Text size="sm">{detail.name || '--'}</Text>
              {detail.isDemoAccount && <DemoAccountPill />}
            </Group>
          </DetailRow>

          <DetailRow label="Email">
            <Text size="sm">{detail.email || '--'}</Text>
          </DetailRow>

          <DetailRow label="Country">
            <Text size="sm">{detail.country || '--'}</Text>
          </DetailRow>

          <DetailRow label="User ID">
            <Text size="xs" ff="monospace" c={semanticColors.text.secondary}>
              {detail.id}
            </Text>
          </DetailRow>

          <Divider />

          {/* Activity Section */}
          <Text fw={600} size="sm" c={semanticColors.text.secondary}>
            ACTIVITY
          </Text>

          <DetailRow label="Signed Up">
            <Text size="sm">{formatDateTime(detail.createdAt)}</Text>
          </DetailRow>

          <DetailRow label="Last Login">
            <Text size="sm">{formatDateTime(detail.lastLoginAt)}</Text>
          </DetailRow>

          <DetailRow label="Contacts">
            <Text size="sm">{detail.contactsCount}</Text>
          </DetailRow>

          <DetailRow label="Scheduled Items">
            <UnstyledButton onClick={() => setSmilesOpened(true)}>
              <Group gap={4} c={brandColors.primary} wrap="nowrap">
                <Text size="sm">{detail.scheduledItemsCount}</Text>
                <Text size="sm">View</Text>
                <IconChevronRight size={14} />
              </Group>
            </UnstyledButton>
          </DetailRow>

          <Divider />

          {/* Attribution Section (PBI #1104) — first-touch ad ledger */}
          <Text fw={600} size="sm" c={semanticColors.text.secondary}>
            ATTRIBUTION
          </Text>

          {!detail.cameViaPaidAd &&
          !detail.acquisitionProvider &&
          !detail.gclid &&
          !detail.gbraid &&
          !detail.wbraid &&
          !detail.msclkid &&
          !detail.fbclid &&
          !detail.oppref &&
          !detail.utmSource &&
          !detail.utmMedium &&
          !detail.utmCampaign &&
          !detail.landingPath ? (
            <Text size="sm" c={semanticColors.text.secondary}>
              Organic / unknown
            </Text>
          ) : (
            <>
              <DetailRow label="Provider">
                <Badge
                  color={getAcquisitionProviderBadgeColor(detail.acquisitionProvider)}
                  size="sm"
                  variant="light"
                >
                  {detail.acquisitionProvider ?? ACQUISITION_PROVIDER_OTHER}
                </Badge>
              </DetailRow>

              <DetailRow label="Paid traffic">
                <Badge
                  color={detail.cameViaPaidAd ? brandColors.success : brandColors.neutral}
                  size="sm"
                  variant="light"
                >
                  {detail.cameViaPaidAd ? 'Yes' : 'No'}
                </Badge>
              </DetailRow>

              {detail.landingPath && (
                <DetailRow label="Landing">
                  <Text size="sm" ff="monospace">
                    {detail.landingPath}
                  </Text>
                </DetailRow>
              )}

              {detail.attributedAtUtc && (
                <DetailRow label="Attributed At">
                  <Text size="sm">{formatDateTime(detail.attributedAtUtc)}</Text>
                </DetailRow>
              )}

              {(
                [
                  ['gclid', detail.gclid],
                  ['gbraid', detail.gbraid],
                  ['wbraid', detail.wbraid],
                  ['msclkid', detail.msclkid],
                  ['fbclid', detail.fbclid],
                  ['oppref', detail.oppref],
                ] as const
              )
                .filter(([, value]) => Boolean(value))
                .map(([label, value]) => (
                  <DetailRow key={label} label={label}>
                    <Text
                      size="xs"
                      ff="monospace"
                      style={{ wordBreak: 'break-all', userSelect: 'all' }}
                      title="Select to copy"
                    >
                      {value}
                    </Text>
                  </DetailRow>
                ))}

              {(
                [
                  ['utm_source', detail.utmSource],
                  ['utm_medium', detail.utmMedium],
                  ['utm_campaign', detail.utmCampaign],
                  ['utm_content', detail.utmContent],
                  ['utm_term', detail.utmTerm],
                ] as const
              )
                .filter(([, value]) => Boolean(value))
                .map(([label, value]) => (
                  <DetailRow key={label} label={label}>
                    <Text size="sm">{value}</Text>
                  </DetailRow>
                ))}
            </>
          )}

          <Divider />

          {/* Cookie consent ledger (PBI #1154) — always visible for Ads reconciliation */}
          <Text fw={600} size="sm" c={semanticColors.text.secondary}>
            COOKIE CONSENT
          </Text>

          <DetailRow label="Marketing cookies">
            <CookieConsentBadge value={detail.cookieMarketingConsent} />
          </DetailRow>

          <DetailRow label="Analytics cookies">
            <CookieConsentBadge value={detail.cookieAnalyticsConsent} />
          </DetailRow>

          <DetailRow label="Consent captured">
            <Text size="sm">
              {detail.cookieConsentCapturedAtUtc
                ? formatDateTime(detail.cookieConsentCapturedAtUtc)
                : '—'}
            </Text>
          </DetailRow>

          {detail.cookieConsentUpdatedAtUtc &&
            detail.cookieConsentUpdatedAtUtc !== detail.cookieConsentCapturedAtUtc && (
              <DetailRow label="Consent updated">
                <Text size="sm">{formatDateTime(detail.cookieConsentUpdatedAtUtc)}</Text>
              </DetailRow>
            )}

          {adsConsentHelper && (
            <Text size="sm" c={semanticColors.text.secondary}>
              {adsConsentHelper}
            </Text>
          )}

          <Divider />

          {/* Subscription Section */}
          <Text fw={600} size="sm" c={semanticColors.text.secondary}>
            SUBSCRIPTION
          </Text>

          <DetailRow label="Plan">
            <Text size="sm">
              {detail.subscriptionPlanName || 'Free'}
            </Text>
          </DetailRow>

          <DetailRow label="Status">
            <Badge
              color={getStatusColor(detail.subscriptionStatus)}
              size="sm"
              variant="light"
            >
              {statusLabel}
            </Badge>
          </DetailRow>

          <DetailRow label="Subscribed At">
            <Text size="sm">{formatDateTime(detail.subscribedAt)}</Text>
          </DetailRow>

          <DetailRow label="Billing Interval">
            <Text size="sm">{detail.billingInterval || '--'}</Text>
          </DetailRow>

          {detail.currentPeriodStart && (
            <DetailRow label="Current Period">
              <Text size="sm">
                {formatDate(detail.currentPeriodStart)} -{' '}
                {formatDate(detail.currentPeriodEnd)}
              </Text>
            </DetailRow>
          )}

          {detail.cancelAtPeriodEnd && (
            <DetailRow label="Cancel at Period End">
              <Badge color={brandColors.warning} size="sm" variant="light">
                Yes
              </Badge>
            </DetailRow>
          )}

          {detail.cancelledAt && (
            <DetailRow label="Cancelled At">
              <Text size="sm">{formatDateTime(detail.cancelledAt)}</Text>
            </DetailRow>
          )}

          <Divider />

          {/* Account Section */}
          <Text fw={600} size="sm" c={semanticColors.text.secondary}>
            ACCOUNT
          </Text>

          <DetailRow label="Onboarding">
            <Badge
              color={
                detail.hasCompletedOnboarding
                  ? brandColors.success
                  : brandColors.neutral
              }
              size="sm"
              variant="light"
            >
              {detail.hasCompletedOnboarding ? 'Completed' : 'Pending'}
            </Badge>
          </DetailRow>

          <DetailRow label="Terms Accepted">
            {detail.termsAcceptedAt ? (
              <Stack gap={0} align="flex-end">
                <Text size="sm">
                  {formatDateTime(detail.termsAcceptedAt)}
                </Text>
                {detail.termsAcceptedVersion && (
                  <Text size="xs" c={semanticColors.text.secondary}>
                    Version {detail.termsAcceptedVersion}
                  </Text>
                )}
              </Stack>
            ) : (
              <Text size="sm">Not accepted</Text>
            )}
          </DetailRow>

          <DetailRow label="AI Regen Count">
            <Stack gap={0} align="flex-end">
              <Text size="sm">{detail.aiRegenerationCount}</Text>
              {detail.aiRegenerationCountResetDate && (
                <Text size="xs" c={semanticColors.text.secondary}>
                  Reset: {formatDate(detail.aiRegenerationCountResetDate)}
                </Text>
              )}
            </Stack>
          </DetailRow>

          {detail.freeContactLimitOverride !== null && (
            <DetailRow label="Free Contact Limit Override">
              <Badge color={brandColors.info} size="sm" variant="light">
                {detail.freeContactLimitOverride}
              </Badge>
            </DetailRow>
          )}

          <Divider label="Marketing emails" labelPosition="center" />

          <Text size="sm" c={semanticColors.text.secondary}>
            Tips, product updates, and upgrade offers for free accounts. Transactional mail
            (welcome message, card and schedule reminders) is not affected.
          </Text>

          <Switch
            label="Marketing emails"
            description="Receive nurture emails with ideas for getting more from Scheduled Smiles"
            checked={detail.marketingEmailsEnabled}
            disabled={savingMarketingEmails || detail.isAccountDisabled}
            onChange={(event) => {
              void handleMarketingEmailsChange(event.currentTarget.checked);
            }}
          />

          <Divider label="Account access" labelPosition="center" />

          <DetailRow label="Account status">
            {detail.isAccountDisabled ? (
              <Stack gap={4} align="flex-end">
                <Badge color={brandColors.danger} size="sm" variant="light">
                  Disabled
                </Badge>
                {detail.accountDisabledAtUtc && (
                  <Text size="xs" c={semanticColors.text.secondary}>
                    Since {formatDateTime(detail.accountDisabledAtUtc)}
                  </Text>
                )}
              </Stack>
            ) : (
              <Badge color={brandColors.success} size="sm" variant="light">
                Active
              </Badge>
            )}
          </DetailRow>

          <Text size="sm" c={semanticColors.text.secondary}>
            Disabled customers cannot sign in or use the API. Scheduled items stay pending
            and resume when the account is re-enabled. Disabling cancels paid subscriptions at
            period end.
          </Text>

          {detail.isAccountDisabled ? (
            <Button
              color={brandColors.success}
              variant="light"
              onClick={() => openAccountStatusConfirm(false)}
              loading={savingAccountStatus}
            >
              Re-enable account
            </Button>
          ) : (
            <Button
              color={brandColors.danger}
              variant="light"
              onClick={() => openAccountStatusConfirm(true)}
              loading={savingAccountStatus}
            >
              Disable account
            </Button>
          )}

          {customerId && (
            <CustomerTestAccountSection
              customerId={customerId}
              opened={opened}
              onChanged={(next) => {
                setDetail((current) =>
                  current ? { ...current, isDemoAccount: next.isDemoAccount } : current,
                );
                onAccountStatusChanged?.();
              }}
            />
          )}
        </Stack>
      ) : null}
    </AppModal>

    <AppModal
      id="customer-disable-confirm"
      opened={accountConfirmOpened}
      onClose={() => {
        if (!savingAccountStatus) {
          setAccountConfirmOpened(false);
        }
      }}
      zIndex={zIndex + 1}
      title={pendingAccountDisabled ? 'Disable customer account?' : 'Re-enable customer account?'}
      size="sm"
      bodyHeight="auto"
      footer={
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => setAccountConfirmOpened(false)}
            disabled={savingAccountStatus}
          >
            Cancel
          </Button>
          <Button
            color={pendingAccountDisabled ? brandColors.danger : brandColors.success}
            onClick={() => {
              void handleAccountStatusConfirm();
            }}
            loading={savingAccountStatus}
          >
            {pendingAccountDisabled ? 'Disable account' : 'Re-enable account'}
          </Button>
        </Group>
      }
    >
      <Stack gap="sm">
        <Text size="sm">
          {pendingAccountDisabled
            ? 'This customer will be signed out immediately and cannot sign in again until you re-enable the account.'
            : 'This customer will be able to sign in and use the app again. Pending scheduled items resume automatically.'}
        </Text>
        {pendingAccountDisabled && detail?.subscriptionStatus === 'Active' && (
          <Text size="sm" c={semanticColors.text.secondary}>
            An active subscription will be set to cancel at the end of the current billing period,
            and the customer will receive a cancellation email.
          </Text>
        )}
        {!pendingAccountDisabled &&
          detail?.cancelAtPeriodEnd &&
          detail.subscriptionStatus === 'Active' && (
            <Text size="sm" c={semanticColors.text.secondary}>
              If this account was disabled by an admin before period end, the pending subscription
              cancellation may be undone and a re-enabled email sent.
            </Text>
          )}
      </Stack>
    </AppModal>

    {smilesOpened ? (
      <Suspense fallback={null}>
        <CustomerScheduledSmilesModal
          userId={customerId}
          opened={smilesOpened && opened}
          onClose={() => setSmilesOpened(false)}
        />
      </Suspense>
    ) : null}
    </>
  );
};
